import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, PencilSimple, CheckCircle, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImportInvoicesDialog from "@/components/ImportInvoicesDialog";
import { useAuth } from "@/context/AuthContext";

const empty = () => ({
  date: new Date().toISOString().slice(0, 10),
  number: "",
  description: "",
  amount: "",
  vat_rate: "20",
  currency: "TRY",
  direction: "outgoing",
  status: "pending",
  due_date: "",
});

export default function ContactInvoices({ contact }) {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty());

  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices", contact.id],
    queryFn: async () => (await api.get(`/contacts/${contact.id}/invoices`)).data,
  });

  const create = useMutation({
    mutationFn: async (p) => (await api.post(`/contacts/${contact.id}/invoices`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries();
      setOpen(false); setEditing(null); setForm(empty());
      toast.success("Fatura eklendi");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Hata"),
  });
  const update = useMutation({
    mutationFn: async ({ id, p }) => (await api.put(`/invoices/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries();
      setOpen(false); setEditing(null); setForm(empty());
      toast.success("Fatura güncellendi");
    },
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/invoices/${id}`)).data,
    onSuccess: () => qc.invalidateQueries(),
  });
  const markPaid = useMutation({
    mutationFn: async (id) => (await api.post(`/invoices/${id}/mark-paid`)).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success("Ödendi olarak işaretlendi"); },
  });

  const startCreate = async () => {
    setEditing(null);
    let nextNo = "";
    try {
      const y = new Date().getFullYear();
      const res = await api.get(`/invoices/next-number?year=${y}`);
      nextNo = res.data?.next_number || "";
    } catch (e) {
      // non-fatal: keep empty
    }
    setForm({
      ...empty(),
      number: nextNo,
      direction: contact.kind === "supplier" ? "incoming" : "outgoing",
    });
    setOpen(true);
  };
  const startEdit = (inv) => {
    setEditing(inv);
    setForm({
      date: inv.date || "",
      number: inv.number || "",
      description: inv.description || "",
      amount: String(inv.amount ?? ""),
      vat_rate: String(inv.vat_rate ?? "20"),
      currency: inv.currency || "TRY",
      direction: inv.direction || "outgoing",
      status: inv.status || "pending",
      due_date: inv.due_date || "",
    });
    setOpen(true);
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      vat_rate: parseFloat(form.vat_rate ?? "20"),
    };
    if (payload.status === "paid") delete payload.due_date;
    if (!payload.number) delete payload.number;
    if (!payload.description) delete payload.description;
    if (!payload.due_date) delete payload.due_date;
    if (editing) update.mutate({ id: editing.id, p: payload });
    else create.mutate(payload);
  };

  // Live net/VAT preview from form.amount (gross) and form.vat_rate
  const _gross = parseFloat(form.amount || 0) || 0;
  const _vatRate = parseFloat(form.vat_rate || 0) || 0;
  const _net = _vatRate ? _gross / (1 + _vatRate / 100) : _gross;
  const _vat = _gross - _net;

  const exportCsv = () =>
    downloadCsv(
      `faturalar-${(contact.name || "cari").toLocaleLowerCase("tr-TR").replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { label: "Tarih", value: (i) => fmtDate(i.date) },
        { label: "Yön", value: (i) => (i.direction === "outgoing" ? "Giden" : "Gelen") },
        { label: "Fatura No", value: (i) => i.number || "" },
        { label: "Açıklama", value: (i) => i.description || "" },
        { label: "Net", value: (i) => i.net_amount },
        { label: "KDV %", value: (i) => i.vat_rate },
        { label: "KDV", value: (i) => i.vat_amount },
        { label: "Brüt", value: (i) => i.amount },
        { label: "Para Birimi", value: (i) => i.currency || "TRY" },
        { label: "Ödenen (TL)", value: (i) => i.paid_amount_try },
        { label: "Kalan (TL)", value: (i) => i.remaining_try },
        { label: "Durum", value: (i) => (i.status === "paid" ? "Ödendi" : "Beklemede") },
      ],
      invoices,
      ["", "", "", "", "", "", "", "", "NET FATURA BAKİYESİ (TL)", "", total, ""]
    );

  const sumDir = (dir) =>
    invoices.filter((i) => i.direction === dir).reduce((s, i) => s + (i.amount_try || 0), 0);
  // Net fatura bakiyesi: Müşteri → Giden - Gelen; Tedarikçi → Gelen - Giden
  const total = contact.kind === "supplier"
    ? sumDir("incoming") - sumDir("outgoing")
    : sumDir("outgoing") - sumDir("incoming");
  const pending = invoices.filter((i) => i.status === "pending")
    .reduce((s, i) => s + (i.amount_try || 0), 0);

  return (
    <div data-testid="contact-invoices">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-3">
          <div className="rounded-md border border-border bg-white px-3 py-2">
            <div className="overline">Net Fatura Bakiyesi</div>
            <div
              className={`mono text-lg font-semibold ${total >= 0 ? "text-emerald-700" : "text-red-600"}`}
              data-testid="invoices-net-total"
            >
              {fmtMoney(total)}
            </div>
          </div>
          <div className="rounded-md border border-border bg-white px-3 py-2">
            <div className="overline">Bekleyen</div>
            <div className="mono text-lg font-semibold text-amber-700">{fmtMoney(pending)}</div>
          </div>
        </div>
        {isAdmin && <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={invoices.length === 0} data-testid="invoice-csv-btn">
            <DownloadSimple size={14} className="mr-1" /> CSV İndir
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="invoice-import-btn">
            <UploadSimple size={14} className="mr-1" /> Toplu Aktar
          </Button>
          <Button data-testid="invoice-add-btn" onClick={startCreate}>
            <Plus size={14} className="mr-1" /> Yeni Fatura
          </Button>
        </div>}
        {importOpen && (
          <ImportInvoicesDialog
            open={importOpen}
            onOpenChange={(v) => !v && setImportOpen(false)}
            contact={contact}
          />
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Fatura Düzenle" : "Yeni Fatura"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tarih</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="invoice-date" /></div>
                <div><Label>Fatura No</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="invoice-number" /></div>
              </div>
              <div>
                <Label>Yön</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger data-testid="invoice-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outgoing">Giden Fatura (benim kestiğim)</SelectItem>
                    <SelectItem value="incoming">Gelen Fatura (bana kesilen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Tutar (Brüt)</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="invoice-amount" /></div>
                <div>
                  <Label>KDV %</Label>
                  <Select value={form.vat_rate} onValueChange={(v) => setForm({ ...form, vat_rate: v })}>
                    <SelectTrigger data-testid="invoice-vat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="1">1%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="TRY">TRY</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {_gross > 0 && (
                <div className="rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2 text-xs" data-testid="invoice-vat-preview">
                  <div className="flex justify-between"><span>Net:</span><span className="mono font-semibold">{fmtMoney(_net, form.currency)}</span></div>
                  <div className="flex justify-between"><span>KDV ({_vatRate}%):</span><span className="mono font-semibold">{fmtMoney(_vat, form.currency)}</span></div>
                  <div className="mt-1 flex justify-between border-t border-border pt-1"><span className="font-semibold">Brüt:</span><span className="mono font-black">{fmtMoney(_gross, form.currency)}</span></div>
                </div>
              )}
              <div>
                <Label>Açıklama</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="invoice-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Durum</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="invoice-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Beklemede</SelectItem>
                      <SelectItem value="paid">Ödendi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.status === "pending" && (
                  <div><Label>Vade</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                )}
              </div>
              <DialogFooter><Button type="submit" data-testid="invoice-save">{editing ? "Güncelle" : "Kaydet"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-left text-xs uppercase text-muted-foreground tracking-wider">
              <th className="px-3 py-2">Tarih</th>
              <th className="px-3 py-2">Yön</th>
              <th className="px-3 py-2">Fatura No</th>
              <th className="px-3 py-2">Açıklama</th>
              <th className="px-3 py-2 text-right">Net</th>
              <th className="px-3 py-2 text-right">KDV</th>
              <th className="px-3 py-2 text-right">Brüt</th>
              <th className="px-3 py-2 text-right">Ödenen (TL)</th>
              <th className="px-3 py-2 text-right">Kalan (TL)</th>
              <th className="px-3 py-2">Durum</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">Henüz fatura yok.</td></tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border/60 hover:bg-secondary/30">
                <td className="px-3 py-2 mono">{fmtDate(inv.date)}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold ${
                      inv.direction === "outgoing"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-indigo-50 text-indigo-700"
                    }`}
                    data-testid={`invoice-dir-${inv.id}`}
                  >
                    {inv.direction === "outgoing" ? "Giden" : "Gelen"}
                  </span>
                </td>
                <td className="px-3 py-2 mono">{inv.number || "-"}</td>
                <td className="px-3 py-2 text-muted-foreground">{inv.description || "-"}</td>
                <td className="px-3 py-2 mono text-right">{fmtMoney(inv.net_amount, inv.currency)}</td>
                <td className="px-3 py-2 mono text-right text-muted-foreground" data-testid={`invoice-vat-${inv.id}`}>
                  {fmtMoney(inv.vat_amount, inv.currency)}
                  <span className="ml-1 text-[10px] uppercase">({inv.vat_rate ?? 20}%)</span>
                </td>
                <td className="px-3 py-2 mono text-right font-semibold">{fmtMoney(inv.amount, inv.currency)}</td>
                <td className="px-3 py-2 mono text-right text-emerald-700" data-testid={`invoice-paid-${inv.id}`}>{fmtMoney(inv.paid_amount_try, "TRY")}</td>
                <td className="px-3 py-2 mono text-right text-amber-700" data-testid={`invoice-remaining-${inv.id}`}>{fmtMoney(inv.remaining_try, "TRY")}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded-sm px-2 py-0.5 text-xs ${
                    inv.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                  }`}>{inv.status === "paid" ? "Ödendi" : "Beklemede"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {isAdmin && <div className="flex justify-end gap-1">
                    {inv.status === "pending" && (
                      <button onClick={() => markPaid.mutate(inv.id)} data-testid={`invoice-markpaid-${inv.id}`}
                        className="rounded border border-border p-1 hover:bg-secondary" title="Ödendi işaretle">
                        <CheckCircle size={12} />
                      </button>
                    )}
                    <button onClick={() => startEdit(inv)} data-testid={`edit-invoice-${inv.id}`}
                      className="rounded border border-border p-1 hover:bg-secondary" title="Düzenle">
                      <PencilSimple size={12} />
                    </button>
                    <button onClick={() => remove.mutate(inv.id)} data-testid={`del-invoice-${inv.id}`}
                      className="rounded border border-border p-1 text-destructive hover:bg-destructive/5">
                      <Trash size={12} />
                    </button>
                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
